import { isNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { BaseQLMiddleware } from '@adornis/baseql/metadata/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { type ClientContext, getContext } from '@adornis/baseql/server/context.js';
import { Person } from '../../users/entities/person.js';
import type { UserOnUserPayload } from '../../users/UserPolicy.js';
import { MARTINUS_ABAC_PAYLOAD } from '../abac-payloads/MartinusAbacPayload.js';
import { MartinusAccessController } from '../api/MartinusAccessController.js';
import type { UserWithPermissionFields } from '../UserWithPermissionFields.js';

const VIEWER_TTL_MS = 60 * 1000;

/**
 * Read raw, as the permission evaluation must not depend on what the viewer is allowed to see.
 */
const loadPermissionFields = async (_id: string): Promise<Maybe<UserWithPermissionFields>> =>
  (await getRawCollection<EntityData<Person>>(Person._collectionName)).findOne(
    { _id },
    { projection: { _id: 1, roles: 1, sorgeberechtigteKinderIDs: 1, spendenKinderIDs: 1 } },
  );

/**
 * The viewer, kept per request context: a list resolves a guarded field once per row, and reading the viewer for
 * each of them would cost a query per row.
 * ponytail: a live subscription keeps its context, so a role change reaches it only after the TTL.
 */
const viewers = new WeakMap<ClientContext, { loadedAt: number; viewer: Promise<Maybe<UserWithPermissionFields>> }>();

const viewerOf = (context: ClientContext) => {
  const cached = viewers.get(context);
  if (cached && Date.now() - cached.loadedAt < VIEWER_TTL_MS) return cached.viewer;
  const viewer = context.userID ? loadPermissionFields(context.userID as string) : Promise.resolve(null);
  viewers.set(context, { loadedAt: Date.now(), viewer });
  return viewer;
};

/**
 * Whether the logged-in viewer may do `action` on `viewedUser`. Server-internal calls (jobs, workers, the view
 * worker) run with full trust.
 */
export async function viewerCanOnPerson(action: string, viewedUser: Maybe<UserWithPermissionFields>) {
  const context = getContext();
  if (!context || context.serverContext) return true;
  const currentUser = await viewerOf(context);
  if (!currentUser) return false;
  return MartinusAccessController.can({
    type: MARTINUS_ABAC_PAYLOAD,
    action,
    currentUser,
    viewedUser,
  } as UserOnUserPayload);
}

/**
 * Withholds a field of a person (`null`) from every viewer the ABAC `action` denies on that person. The person
 * document itself stays readable - this is for the parts of it that need more than the publish filter grants.
 */
export const abacFieldMiddleware =
  (action: string): BaseQLMiddleware =>
  async ({ src, next }) => {
    const value: unknown = await next();
    // nothing to hide - spares the evaluation for the many persons without the field
    if (isNullish(value)) return value;

    const person = src as Partial<UserWithPermissionFields>;
    if (!person._id) return null;
    // rows of a projected read may lack the roles the policies judge by
    const viewedUser = person.roles ? (person as UserWithPermissionFields) : await loadPermissionFields(person._id);
    return (await viewerCanOnPerson(action, viewedUser)) ? value : null;
  };
